<template>
  <div id="page-user-list">
    <vx-card
      ref="filterCard"
      :title="$t('users.filters')"
      class="user-list-filters mb-8"
      @refresh="resetColFilters"
      refreshContentAction
      collapseAction
    >
      <div class="vx-row">
        <div class="vx-col md:w-1/2 w-full">
          <label class="text-sm opacity-75"
            >{{ $t('forms.search') }}: {{ $t('users.username') }}, {{ $t('users.first_name') }},
            {{ $t('users.last_name') }}
          </label>
          <vs-input
            class="w-full"
            v-model="searchString"
            @input="updateSearchString"
            :placeholder="$t('forms.type_search')"
          />
        </div>
        <div class="vx-col md:w-1/4 w-full">
          <label class="text-sm opacity-75">{{ $t('users.addresses.province') }}</label>
          <v-select
            :options="provinceOptions"
            :clearable="false"
            taggable
            multiple
            :placeholder="$t('forms.select_option')"
            :dir="$vs.rtl ? 'rtl' : 'ltr'"
            v-model="provinceFilter"
            class="w-full"
          />
        </div>
        <div class="vx-col md:w-1/4 w-full">
          <label class="text-sm opacity-75">{{ $t('system.roles.roles') }}</label>
          <v-select
            :options="roleOptions"
            :clearable="false"
            taggable
            multiple
            :placeholder="$t('forms.select_option')"
            :dir="$vs.rtl ? 'rtl' : 'ltr'"
            v-model="roleFilter"
            class="w-full"
          />
        </div>
        <div class="vx-col md:w-1/4 w-full">
          <label class="text-sm opacity-75">{{ $t('entity.states.states') }}</label>
          <v-select
            :options="stateOptions"
            :clearable="false"
            taggable
            multiple
            :placeholder="$t('forms.select_option')"
            :dir="$vs.rtl ? 'rtl' : 'ltr'"
            v-model="stateFilter"
            class="mb-4 md:mb-0"
          />
        </div>
      </div>
    </vx-card>

    <vs-table
      ref="table"
      :max-items="100"
      :data="usersData"
      :total="paginationPageSize"
      @sort="handleSort"
      @selected="handleSelected"
      class="card-table"
    >
      <div slot="header" class="flex flex-wrap-reverse items-center flex-grow justify-between">
        <!-- ITEMS PER PAGE -->
        <vs-dropdown vs-trigger-click class="cursor-pointer" v-if="usersDataCount">
          <div
            class="p-4 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bg cursor-pointer flex items-center justify-between font-medium"
          >
            <span class="mr-2"
              >{{ (currentPage * paginationPageSize - (paginationPageSize - 1)) | persian_digits }}
              {{ $t('forms.to') }}
              {{
                usersDataCount - currentPage * paginationPageSize > 0
                  ? currentPage * paginationPageSize
                  : usersDataCount | persian_digits
              }}
              {{ $t('forms.from') }} {{ usersDataCount | persian_digits }}</span
            >
            <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
          </div>
          <vs-dropdown-menu>
            <vs-dropdown-item @click="paginationSetPageSize(5)">
              <span>{{ 5 | persian_digits }}</span>
            </vs-dropdown-item>
            <vs-dropdown-item @click="paginationSetPageSize(10)">
              <span>{{ 10 | persian_digits }}</span>
            </vs-dropdown-item>
            <vs-dropdown-item @click="paginationSetPageSize(50)">
              <span>{{ 50 | persian_digits }}</span>
            </vs-dropdown-item>
            <vs-dropdown-item @click="paginationSetPageSize(100)">
              <span>{{ 100 | persian_digits }}</span>
            </vs-dropdown-item>
          </vs-dropdown-menu>
        </vs-dropdown>
        <!-- PAGINATION -->
        <div>
          <vs-pagination :total="totalPages" :max="7" v-model="currentPage" />
        </div>
      </div>

      <template slot="thead">
        <vs-th sort-key="username">{{ $t('users.username') }}</vs-th>
        <vs-th sort-key="details.firstName">{{ $t('users.first_name') }}</vs-th>
        <vs-th sort-key="details.lastName">{{ $t('users.last_name') }}</vs-th>
        <vs-th sort-key="roles">{{ $t('system.roles.roles') }}</vs-th>
        <vs-th sort-key="state">{{ $t('entity.state') }}</vs-th>
        <vs-th>{{ $t('forms.actions') }}</vs-th>
      </template>

      <template slot-scope="{ data }">
        <tbody id="table-body" v-if="data.length">
          <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
            <vs-td>
              <div class="flex items-center">
                <avatar v-if="tr.avatar" :id="tr.avatar" class="flex-shrink-0 mr-2" size="30px" />
                {{ tr.username | persian_digits }}
              </div>
            </vs-td>
            <vs-td>
              {{ tr.details ? tr.details.firstName : '' }}
            </vs-td>
            <vs-td>
              {{ tr.details ? tr.details.lastName : '' }}
            </vs-td>
            <vs-td>
              <CellBadges :data="tr.roles" local="system.roles." />
            </vs-td>
            <vs-td>
              <CellBadges :data="tr.state" local="entity.states." />
            </vs-td>
            <vs-td class="whitespace-no-wrap">
              <CellActions :user="tr" />
            </vs-td>
          </vs-tr>
        </tbody>
      </template>
    </vs-table>
    <!-- PAGINATION -->
    <div>
      <vs-pagination :total="totalPages" :max="7" v-model="currentPage" />
    </div>
  </div>
</template>

<script>
// Store Module
import moduleUserManagement from '@/store/user-management/moduleUserManagement.js'

//Cells
import CellActions from './CellActions'
import CellBadges from '@/views/components/common/table-cells/CellBadges.vue'
import Avatar from '@/views/components/common/Avatar'

export default {
  components: {
    CellActions,
    CellBadges,
    Avatar
  },
  data() {
    return {
      // Filter Options
      roleFilter: [],
      stateFilter: [],
      provinceFilter: [],
      query: {},
      searchString: '',
      //Sort
      sort: {},
      currentPage: 1,
      provinceOptions: [
        { value: 'استان کرمان', label: 'کرمان' },
        { value: 'استان خراسان رضوی', label: 'خراسان رضوی' },
        { value: 'استان تهران', label: 'تهران' },
        { value: 'استان خراسان شمالی', label: 'خراسان شمالی' },
        { value: 'استان ایلام', label: 'ایلام' },
        { value: 'استان لرستان', label: 'لرستان' },
        { value: 'استان مرکزی', label: 'مرکزی' },
        { value: 'استان چهارمحال و بختیاری', label: 'چهارمحال و بختیاری' },
        { value: 'استان کرمانشاه', label: 'کرمانشاه' },
        { value: 'استان همدان', label: 'همدان' },
        { value: 'استان قزوین', label: 'قزوین' },
        { value: 'استان گیلان', label: 'گیلان' },
        { value: 'استان زنجان', label: 'زنجان' },
        { value: 'استان سمنان', label: 'سمنان' },
        { value: 'استان اصفهان', label: 'اصفهان' },
        { value: 'استان کهگیلویه و بویر احمد', label: 'کهگیلویه و بویر احمد' },
        { value: 'استان کردستان', label: 'کردستان' },
        { value: 'استان آذربایجان غربی', label: 'آذربایجان غربی' },
        { value: 'استان فارس', label: 'فارس' },
        { value: 'استان بوشهر', label: 'بوشهر' },
        { value: 'استان اردبیل', label: 'اردبیل' },
        { value: 'استان مازندران', label: 'مازندران' },
        { value: 'استان گلستان', label: 'گلستان' },
        { value: 'استان خراسان جنوبی', label: 'خراسان جنوبی' },
        { value: 'استان سیستان و بلوچستان', label: 'سیستان و بلوچستان' },
        { value: 'استان قم', label: 'قم' },
        { value: 'استان البرز', label: 'البرز' },
        { value: 'استان آذربایجان شرقی', label: 'آذربایجان شرقی' },
        { value: 'استان یزد', label: 'یزد' },
        { value: 'استان هرمزگان', label: 'هرمزگان' },
        { value: 'استان خوزستان', label: 'خوزستان' }
      ]
    }
  },
  watch: {
    roleFilter(obj) {
      this.setColumnFilter('roles', obj)
    },
    stateFilter(obj) {
      this.setColumnFilter('state', obj)
    },
    provinceFilter(obj) {
      this.setColumnFilter('province', obj)
    },
    currentPage(val) {
      this.$store.commit('userManagement/SET_USERS_SKIP', (val - 1) * this.paginationPageSize)
      this.updateTable()
    }
  },
  computed: {
    stateOptions() {
      return this.$store.getters['enum/selectOptions']('State', 'entity.states')
    },
    roleOptions() {
      return this.$store.getters['enum/selectOptions']('Role', 'system.roles')
    },
    usersDataCount() {
      return this.$store.state.userManagement.usersCount
    },
    usersData() {
      return this.$store.state.userManagement.users
    },
    paginationPageSize() {
      return this.$store.state.userManagement.limit
    },
    totalPages() {
      return this.$store.getters['userManagement/totalPages']
    }
  },
  methods: {
    setQuery() {
      this.$store.commit('userManagement/SET_USERS_QUERY', this.query)
      this.currentPage = 1
      this.updateTable()
    },
    updateTable() {
      this.$vs.loading({
        scale: 0.6
      })
      this.$store.dispatch('userManagement/fetchUsers').then(() => {
        this.$vs.loading.close()
      })
      this.$store.dispatch('userManagement/countUsers')
    },
    paginationSetPageSize(items) {
      this.$store.commit('userManagement/SET_USERS_LIMIT', items)
      this.updateTable()
    },
    setColumnFilter(column, val) {
      if (val.length > 0) {
        const filter = []
        let i = null
        for (i in val) {
          filter.push(val[i].value ? val[i].value : val[i].label)
        }
        this.query[column] = { $in: filter }
      } else {
        delete this.query[column]
      }
      this.setQuery()
    },
    resetColFilters() {
      this.roleFilter = []
      this.stateFilter = []
      this.provinceFilter = []
      this.searchString = ''
      this.query = {}
      this.setQuery()
      this.$refs.filterCard.removeRefreshAnimation()
    },
    updateSearchString() {
      const regex = { $regex: `/*${this.searchString}/*` }
      this.query['$or'] = [{ username: regex }, { 'details.firstName': regex }, { 'details.lastName': regex }]

      this.setQuery()
    },
    handleSort(col, active) {
      if (col === null) return
      if (active !== null) {
        this.sort[col] = active === 'asc' ? 1 : -1
        this.query['sort'] = this.sort
      } else {
        delete this.query['sort']
      }
      this.setQuery()
    },
    handleSelected(user) {
      this.$router.push({ name: 'system-user-view', params: { id: user.id } })
    }
  },
  created() {
    if (!moduleUserManagement.isRegistered) {
      this.$store.registerModule('userManagement', moduleUserManagement)
      moduleUserManagement.isRegistered = true
    }

    this.updateTable()
  }
}
</script>

<style lang="scss">
#page-user-list {
}
</style>
